/**
 * Anfrage-Dialog der Produktseite.
 *
 * OHNE DIESES SKRIPT FUNKTIONIERT DIE ANFRAGE VOLLSTÄNDIG: Jeder Auslöser ist
 * ein <a href> auf die Kontaktseite mit vorbelegtem Produktbezug. Das Skript
 * fängt den Klick nur ab und zeigt dasselbe Formular ohne Seitenwechsel. Es
 * ist eine Abkürzung, kein Weg.
 *
 * DER RÜCKLAUF IST DER TEIL, DER SONST SPÄT AUFFÄLLT: Shopifys Kontaktformular
 * leitet nach erfolgreichem Absenden auf die Produktseite zurück, Liquid
 * markiert die Bestätigung mit `data-hy-inquiry-result`. Bei einem
 * Validierungsfehler rendert Shopify stattdessen /contact mit den Fehlern (live
 * geprüft, AP-256). Ohne Zutun stünde man
 * danach wieder oben auf der Produktseite, und das Absenden wirkte folgenlos.
 * Deshalb öffnet sich der Dialog nach dem Rücklauf von selbst wieder und
 * zeigt die Bestätigung.
 */

const AUSLOESER = '[data-hy-inquiry]';

const RELEASED_EVENT = 'hy:inquiry:released';

window.HY = window.HY || {};

/**
 * Registers `instance` as the page's active inquiry dialog unless another
 * connected instance already holds that role. Returns true when `instance`
 * is (now) the active one.
 */
function claimActiveInstance(instance) {
  const current = window.HY.inquiryDialog;
  if (current && current !== instance && current.isConnected) return false;
  window.HY.inquiryDialog = instance;
  return true;
}

/**
 * Clears the registration when the active instance leaves the DOM and lets a
 * standby instance take over.
 */
function releaseActiveInstance(instance) {
  if (window.HY.inquiryDialog !== instance) return;
  window.HY.inquiryDialog = null;
  document.dispatchEvent(new CustomEvent(RELEASED_EVENT));
}

class HyInquiryDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onReleased = this.onReleased.bind(this);

    // Every instance consumes its post-back marker, the standby one too:
    // the marker keeps a dialog visible via CSS, and a standby dialog would
    // otherwise stay on screen for good.
    this.posted = this.consumeResultMarkers();

    this.querySelectorAll('[data-hy-inquiry-close]').forEach((b) =>
      b.addEventListener('click', this.onClose),
    );
    // No light dismiss: a click on the dialog's own padding also targets the
    // <dialog> element, so a click beside a field closed a half-filled form
    // (design review round 4). Closing is ESC (native) or the close button.

    // Single-instance guard: the inquiry section and the FAQ contact card can
    // both render a dialog on the same page. Only the first connected instance
    // answers trigger clicks and restores the post-submit state; otherwise one
    // click would open two modals on top of each other. A standby instance
    // takes over when the active one leaves the DOM (e.g. editor re-render).
    if (!claimActiveInstance(this)) {
      document.addEventListener(RELEASED_EVENT, this.onReleased);
      return;
    }

    // Delegiert statt je Auslöser gebunden: Die Sticky-Kaufleiste spiegelt den
    // Knopf, und der Kaufbereich wird beim Variantenwechsel ausgetauscht — an
    // fest gebundenen Handlern hinge danach ein totes Element.
    document.addEventListener('click', this.onTriggerClick);

    this.restoreAfterSubmit();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener(RELEASED_EVENT, this.onReleased);
    releaseActiveInstance(this);
    this.querySelectorAll('[data-hy-inquiry-close]').forEach((b) =>
      b.removeEventListener('click', this.onClose),
    );
  }

  onReleased() {
    if (!claimActiveInstance(this)) return;
    document.removeEventListener(RELEASED_EVENT, this.onReleased);
    document.addEventListener('click', this.onTriggerClick);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest(AUSLOESER);
    if (!trigger) return;

    // Neuer Tab, Mittelklick oder Zusatztaste: Der Besucher will die
    // Kontaktseite wirklich öffnen. Dann nicht abfangen.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.open();
  }

  open() {
    if (!this.dialog) return;
    // showModal() throws on a dialog that is already open, so close it first.
    if (this.dialog.open) this.dialog.close();
    this.dialog.showModal();

    const erstes = this.dialog.querySelector('input:not([type="hidden"]), textarea');
    if (erstes) erstes.focus();

    document.dispatchEvent(new CustomEvent('hy:inquiry:opened', { bubbles: true }));
  }

  onClose(event) {
    event.preventDefault();
    if (this.dialog && this.dialog.open) this.dialog.close();
  }

  /**
   * Removes the post-back markers Liquid renders next to the confirmation
   * and reports whether there were any. The markup is the source of truth, not
   * `?contact_posted=true`: it is what Liquid actually rendered.
   */
  consumeResultMarkers() {
    const results = this.dialog.querySelectorAll('[data-hy-inquiry-result]');
    results.forEach((result) => result.removeAttribute('data-hy-inquiry-result'));
    return results.length > 0;
  }

  restoreAfterSubmit() {
    if (this.posted) this.open();
  }
}

if (!customElements.get('hy-inquiry-dialog')) {
  customElements.define('hy-inquiry-dialog', HyInquiryDialog);
}
